/**
 * @fileoverview The one place Stripe.js is loaded — and the one place that can
 * say whether it is loadable at all.
 *
 * It exists because of two defects, both of which were invisible in production.
 *
 * **1. A missing key rendered nothing, silently.** Both checkout bodies built
 * their Stripe promise at module scope as `KEY ? loadStripe(KEY) : null` and
 * handed the result straight to `EmbeddedCheckoutProvider`. The SDK parses a
 * null `stripe` prop as `{tag: "empty"}` and then takes neither the sync nor
 * the async branch, so the checkout is never initialised: `EmbeddedCheckout`
 * renders an empty `<div>` with no error, no console warning and no network
 * call. `REACT_APP_STRIPE_PUBLISHABLE_KEY` was never set in either CI build,
 * so every upgrade dialog in production was a blank box under "Back to plans"
 * — and nothing, anywhere, said so. `stripeConfigured()` is what lets the
 * callers say it instead of showing the void.
 *
 * **2. Importing the SDK loaded Stripe.js on every page.** The main
 * `@stripe/stripe-js` entry injects `js.stripe.com/v3` from a module-level
 * `Promise.resolve().then(...)` — on IMPORT, whether or not `loadStripe` is
 * ever called, and whether or not a key exists. Nothing in this app is
 * code-split, so `AccountMenu` → `StripeCheckout` → that import put Stripe.js
 * and its `__stripe_mid` cookie on the public landing page. `@stripe/stripe-js/pure`
 * has no such side effect: the script is fetched by the first `loadStripe()`
 * call and not before. That is what keeps the "necessary for fraud prevention"
 * basis `cookieConsent.js` declares for `__stripe_mid`/`__stripe_sid` honest —
 * it only holds while Stripe.js is confined to checkout. Loading it lazily is
 * therefore a PREREQUISITE of setting the key, not a tidy-up beside it.
 *
 * The promise is memoised because `EmbeddedCheckoutProvider` refuses a changed
 * `stripe` prop ("You cannot change the `stripe` prop after setting it"), so
 * every caller and every re-render must be handed the same object.
 */

import { loadStripe } from "@stripe/stripe-js/pure";

/** Read at call time, never captured at import — see the memo note above. */
const publishableKey = () => import.meta.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

/**
 * Whether this build carries a publishable key, i.e. whether a Stripe checkout
 * can render at all. Callers use it to explain a misconfigured build rather
 * than mounting a provider that will sit blank forever.
 *
 * @returns {boolean}
 */
export const stripeConfigured = () => Boolean(publishableKey());

let stripePromise = null;

/**
 * The memoised Stripe instance, or null when this build has no key.
 *
 * The FIRST call is what fetches `js.stripe.com/v3`, so call it from the
 * checkout step and not from module scope.
 *
 * @returns {Promise<import("@stripe/stripe-js").Stripe | null> | null}
 */
export const getStripe = () => {
  const key = publishableKey();
  if (!key) return null;
  if (!stripePromise) stripePromise = loadStripe(key);
  return stripePromise;
};
